import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {ChevronsUpDown} from "lucide-react";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "../ui/command";
import {cn} from "../ui/lib/utils";
import React, {ReactNode, useEffect, useRef} from "react";
import {DropDownItem} from "./MultiSelectorDropdown/MultiSelectorDropDown";

const DefaultDisplaySettings: SingleSelectorSearchableDropdownDisplaySettings = {
    smallSize: true,
    borderClassName: "border",
    className: ""
}

class SingleSelectorSearchableDropdownDisplaySettings {
    smallSize: boolean = false
    borderClassName: string = "border"
    className: string = ""
    dropdownSide?: "top" | "bottom" = "bottom"
    dropdownAlign?: "start" | "center" | "end" = "end"
    dropdownAlignOffset?: number = 0
    dropdownSideOffset?: number = 5
}

export interface SingleSelectorSearchableDropdownProps {
    displaySettings?: SingleSelectorSearchableDropdownDisplaySettings;
    noMatchString: ReactNode;
    possibleItems: DropDownItem[];
    searchPlaceholder: string;
    selected: DropDownItem;
    setSelected: (item: DropDownItem) => void;
    allowAnyString?: boolean;
    disableWildcard?: boolean;
}

export function SingleSelectorSearchableDropdown(props: SingleSelectorSearchableDropdownProps) {
    const [openDropdown, setOpenDropdown] = React.useState(false);
    const [matches, setMatches] = React.useState<DropDownItem[]>(props.possibleItems);
    const [currSearch, setCurrSearch] = React.useState("");
    const [searchableItems, setSearchableItems] = React.useState<DropDownItem[]>(props.possibleItems);
    const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
    const [lastNavigationMethod, setLastNavigationMethod] = React.useState<'mouse' | 'keyboard'>('mouse');
    const listRef = useRef<HTMLDivElement>(null);

    // Wildcard item
    const wildcardItem: DropDownItem = {
        displayName: "*",
        value: "*"
    };

    useEffect(() => {
        let dropDownItems = [...props.possibleItems];

        // Add wildcard option to the top if it doesn't exist and if not disabled
        if (!props.disableWildcard && !dropDownItems.some(item => item.value === "*")) {
            dropDownItems.unshift(wildcardItem);
        }

        setSearchableItems(dropDownItems);
    }, [props.possibleItems, props.disableWildcard]);

    let displaySettings = props.displaySettings || DefaultDisplaySettings;

    // Update matches when dropdown is opened or search changes
    useEffect(() => {
        if (currSearch !== undefined && currSearch !== "") {
            // Filter items based on search text, including wildcard only if it matches
            const filteredItems = searchableItems.filter((item) => {
                // Only keep wildcard if it matches the search or if search is "*"
                if (item.value === "*") {
                    return currSearch === "*" || currSearch.trim() === "";
                }
                // Filter other items based on the search text
                return item.displayName?.toLowerCase().includes(currSearch.toLowerCase());
            });
            setMatches(filteredItems);
            setHighlightedIndex(filteredItems.length > 0 ? 0 : -1);
            setLastNavigationMethod('keyboard');
        } else {
            setMatches(searchableItems);
            setHighlightedIndex(-1);
        }
    }, [currSearch, searchableItems]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current && lastNavigationMethod === 'keyboard') {
            const highlightedElement = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
            if (highlightedElement) {
                highlightedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, lastNavigationMethod]);

    // Clear search when dropdown is opened
    const handleOpenChange = (open: boolean) => {
        setOpenDropdown(open);
        if (open) {
            setCurrSearch("");
            setHighlightedIndex(-1);
            setLastNavigationMethod('mouse');
        }
    };

    // Handle command input key events (for keyboard navigation)
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const items = matches;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                setHighlightedIndex(prev => 
                    prev < items.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : items.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();

                // Special case for wildcard (*) - only if not disabled
                if (currSearch === "*" && !props.disableWildcard) {
                    const wildcardOption = searchableItems.find(item => item.value === "*");
                    if (wildcardOption) {
                        props.setSelected(wildcardOption);
                        setOpenDropdown(false);
                        return;
                    }
                }

                // Select the highlighted item if present
                if (highlightedIndex >= 0 && highlightedIndex < items.length) {
                    props.setSelected(items[highlightedIndex]);
                    setOpenDropdown(false);
                    return;
                }

                // Otherwise, handle as before for custom input
                if (props.allowAnyString && currSearch && currSearch !== "*") {
                    const exactMatch = searchableItems.find(item => 
                        item.displayName?.toLowerCase() === currSearch.toLowerCase()
                    );

                    if (!exactMatch) {
                        // If no exact match, use the custom value
                        props.setSelected({
                            displayName: currSearch,
                            value: currSearch
                        });
                        setOpenDropdown(false);
                    }
                }
                break;
        }
    };

    return <div className={cn("items-center flex flex-col grow justify-center")}>
        <Popover open={openDropdown} modal={true} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild
                            className={cn("flex",
                                displaySettings.smallSize ? "h-5" : "",
                                props.displaySettings?.borderClassName
                            )}>
                <div
                    role="combobox"
                    aria-expanded={openDropdown}
                    className={cn("hover:cursor-pointer hover:text-white flex w-full items-center justify-between text-textmedium px-2 text-xl h-[48px]",
                        displaySettings.smallSize ? "text-xs h-3" : "",
                        displaySettings.className)}
                    onClick={() => handleOpenChange(!openDropdown)}
                >
                    {props.selected.displayName}
                    <ChevronsUpDown
                        className={cn("ml-2 h-4 w-4 shrink-0 opacity-50",
                            displaySettings.smallSize ? "h-2 w-2" : "")}/>
                </div>
            </PopoverTrigger>
            <PopoverContent side={props.displaySettings? props.displaySettings?.dropdownSide : "bottom"}
                            align={props.displaySettings ? props.displaySettings?.dropdownAlign : "end"}
                            alignOffset={props.displaySettings ? props.displaySettings?.dropdownAlignOffset : 0}
                            sideOffset={props.displaySettings ? props.displaySettings?.dropdownSideOffset : 5}
                            avoidCollisions={true}
                            onFocusOutside={() => setOpenDropdown(false)}
                            onEscapeKeyDown={() => setOpenDropdown(false)}
                            onInteractOutside={() => setOpenDropdown(false)}
                            className="text-textlight bg-backgroundmedium max-w-[50vw] rounded">
                <Command onKeyDown={handleKeyDown} shouldFilter={false}>
                    <CommandInput 
                        placeholder={props.searchPlaceholder} 
                        value={currSearch} 
                        id={"free_text_input"}
                        onChangeCapture={(e) => {
                            setCurrSearch(e.currentTarget.value)
                        }}
                        className={cn("h-12 grow text-textlight shadow-none bg-backgroundmedium")}
                        autoFocus
                    />
                    <CommandList ref={listRef} className={"text-textlight"}>
                        <CommandEmpty>
                            {props.allowAnyString ? (
                                <div className="px-2 py-3 text-xs text-textdark">
                                    {props.noMatchString}
                                    {currSearch && currSearch !== "*" && (
                                        <div className="mt-1 text-primary">
                                            Press Enter to use "{currSearch}"
                                        </div>
                                    )}
                                </div>
                            ) : (
                                props.noMatchString
                            )}
                        </CommandEmpty>
                        <CommandGroup>
                            {
                                matches.map((match, index) => {
                                    const isHighlighted = highlightedIndex === index;
                                    
                                    return <CommandItem
                                        className={cn(
                                            "w-full",
                                            isHighlighted && "bg-secondarytransparenter border border-secondary",
                                            lastNavigationMethod === 'mouse' && "hover:bg-secondarytransparenter hover:border hover:border-secondary"
                                        )}
                                        key={index}
                                        data-index={index}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(index);
                                            setLastNavigationMethod('mouse');
                                        }}
                                        onSelect={() => {
                                            setOpenDropdown(false);
                                            props.setSelected(match);
                                        }}>
                                        {match.displayName}
                                    </CommandItem>
                                })
                            }
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    </div>
}
